'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Loader2, AlertCircle, ShieldCheck } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface AdminLoginFormData {
  username: string;
  password: string;
}

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    mode: 'onBlur',
  });

  const onSubmit = async (data: AdminLoginFormData) => {
    setIsLoading(true);
    setLoginError(null);

    try {
      const response = await fetch('/api/admin-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: data.username, password: data.password }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Kredensial tidak valid.');
      }

      // Save admin session to localStorage
      localStorage.setItem('adminSession', JSON.stringify(result.admin));

      router.push('/admin/dashboard');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Terjadi kesalahan. Coba lagi.';
      setLoginError(message);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
            <ShieldCheck size={20} className="text-red-700" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              SMK Negeri 5 Malang
            </p>
            <h1 className="text-xl font-bold text-foreground">Login Admin</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Area terbatas — hanya untuk{' '}
          <strong className="text-foreground">administrator</strong> dan{' '}
          <strong className="text-foreground">staf tata usaha</strong> yang berwenang.
        </p>
      </div>

      {/* Security notice */}
      <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 border border-amber-200 mb-6">
        <ShieldCheck size={15} className="text-amber-700 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <strong>Keamanan:</strong> Akses admin dicatat dan dimonitor. Jangan bagikan
          kredensial ini kepada siapapun.
        </p>
      </div>

      {/* Error alert */}
      {loginError && (
        <div
          className={`flex items-start gap-3 p-3.5 rounded-xl bg-red-50 border border-red-200 mb-5 ${
            isShaking ? 'animate-shake' : ''
          }`}
        >
          <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 leading-snug">{loginError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Username Field */}
        <div>
          <label
            htmlFor="admin-username"
            className="block text-sm font-semibold text-foreground mb-1.5"
          >
            Username Admin
            <span className="text-red-500 ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Username yang diberikan oleh administrator sistem sekolah.
          </p>
          <input
            id="admin-username"
            type="text"
            autoComplete="username"
            placeholder="Masukkan username admin"
            className={`w-full px-4 py-3 rounded-xl border bg-white text-sm font-medium text-foreground placeholder:text-muted-foreground/60 transition-all duration-150 input-focus-admin ${
              errors.username
                ? 'border-red-400 bg-red-50/50' :'border-border hover:border-red-300'
            }`}
            {...register('username', {
              required: 'Username wajib diisi.',
              minLength: {
                value: 3,
                message: 'Username minimal 3 karakter.',
              },
            })}
          />
          {errors.username && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={11} />
              {errors.username.message}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor="admin-password"
              className="block text-sm font-semibold text-foreground"
            >
              Password
              <span className="text-red-500 ml-1">*</span>
            </label>
            <span className="text-xs text-red-700 font-medium cursor-pointer hover:underline">
              Reset password
            </span>
          </div>
          <div className="relative">
            <input
              id="admin-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Masukkan password admin"
              className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white text-sm font-medium text-foreground placeholder:text-muted-foreground/60 transition-all duration-150 input-focus-admin ${
                errors.password
                  ? 'border-red-400 bg-red-50/50' :'border-border hover:border-red-300'
              }`}
              {...register('password', {
                required: 'Password wajib diisi.',
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all duration-150"
              aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={11} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="admin-remember"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-red-600 cursor-pointer"
          />
          <label
            htmlFor="admin-remember"
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            Ingat sesi login (disarankan tidak dicentang di komputer bersama)
          </label>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 btn-admin disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '52px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Memverifikasi...</span>
            </>
          ) : (
            <>
              <LogIn size={18} />
              <span>Masuk ke Panel Admin</span>
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-5 leading-relaxed">
        Akses bermasalah?{' '}
        <span className="text-red-700 font-medium cursor-pointer hover:underline">
          Hubungi administrator sistem
        </span>
      </p>
    </div>
  );
}